import { Suspense, lazy } from "react";
import { Routes, Route, useParams, Navigate } from "react-router-dom";
import { ProbestackSpinner } from "./components/shared/probestack-spinner/Spinner";

const APIDeploy = lazy(() => import("./pages/APIDeploy"));
const APITest = lazy(() => import("./pages/APITest"));
const Onboarding = lazy(() => import("./pages/Onboarding/OrganizationsIndex"));
const BusinessUnitDetail = lazy(() => import("./pages/Onboarding/OrganizationDetail"));
const ProjectDetail = lazy(() => import("./pages/Onboarding/ProjectDetail"));
const ApplicationDetail = lazy(() => import("./pages/Onboarding/ApplicationDetail"));
const AccessCenter = lazy(() => import("./pages/Onboarding/AccessCenter"));
// Redesigned, live-data dashboard (two tabs: AI Gateway / MCP Gateway).
// The original static-data dashboard still lives at ./pages/Dashboard.jsx,
// kept unrouted as a reference/rollback — see src/pages/DashboardLive.jsx.
const Dashboard = lazy(() => import("./pages/DashboardLive"));
const Profile = lazy(() => import("./pages/Profile"));
const SecurityCenter = lazy(() => import("./pages/SecurityCenter"));
const AIRoutes = lazy(() => import("./pages/AIRoutes"));
const AIProxies = lazy(() => import("./pages/Gateway/AIProxies"));
const ProductsView = lazy(() => import("./pages/Gateway/Product"));
const ConsumersView = lazy(() => import("./pages/Gateway/Consumer"));
const GatewayDeveloper = lazy(() => import("./pages/Gateway/Developer"));
const ResourceSyncWizard = lazy(() => import("./pages/Gateway/ResourceSync/ResourceSyncWizard"));

// /ai-gateway/:pageId/sync -> ForgeSphere sync wizard scope (see
// pages/Gateway/ResourceSync/shared.jsx SYNC_SCOPES).
const SYNC_SCOPE_BY_PAGE = {
    "ai-proxy": "ai-proxies",
    "mcp-proxy": "mcp-proxies",
    "agent-proxy": "agent-proxies",
    product: "api-products",
    consumer: "developer-apps",
    developer: "developers",
};
const GovernanceView = lazy(() => import("./pages/Gateway/GovernanceView"));
const AgentGovernance = lazy(() => import("./pages/Gateway/AgentGovernance"));
const AiGovernance = lazy(() => import("./pages/Gateway/AiGovernance"));
const ExceptionManagement = lazy(() => import("./pages/Gateway/ExceptionManagement"));
const FrameworkView = lazy(() => import("./pages/Gateway/FrameworkView"));
const AIProxyMetrics = lazy(() => import("./pages/Gateway/Observability/AIProxyMetrics"));
const ErrorCodeAnalysis = lazy(() => import("./pages/Gateway/Observability/ErrorCodeAnalysis"));
const LatencyAnalysis = lazy(() => import("./pages/Gateway/Observability/LatencyAnalysis"));
const CachePerformance = lazy(() => import("./pages/Gateway/Observability/CachePerformance"));
const TargetPerformance = lazy(() => import("./pages/Gateway/Observability/TargetPerformance"));
const ObservabilityReport = lazy(() => import("./pages/Gateway/Observability/Report"));
const Monitoring = lazy(() => import("./pages/Gateway/Observability/Monitoring"));
const CostControl = lazy(() => import("./pages/Gateway/Observability/CostControl"));
const GatewayTrace = lazy(() => import("./pages/Gateway/Observability/Trace"));
const EnterpriseAudit = lazy(() => import("./pages/Gateway/Observability/EnterpriseAudit"));
const EnvironmentsConfig = lazy(() => import("./pages/Gateway/EnvironmentsConfig"));
const GatewayEnvironments = lazy(() => import("./pages/Gateway/GatewayEnvironments"));
const CicdAutomation = lazy(() => import("./pages/CicdAutomation/CicdAutomationPage"));
const Resilience = lazy(() => import("./pages/Resilience"));
const RAG = lazy(() => import("./pages/RAG"));
const Agents = lazy(() => import("./pages/Agents"));
const AgentHarness = lazy(() => import("./pages/Agentic/AgentHarness"));
const AgentInventory = lazy(() => import("./pages/Agentic/AgentInventory"));
const AgentIdentity = lazy(() => import("./pages/Agentic/AgentIdentity"));
const AgentRbac = lazy(() => import("./pages/Agentic/AgentRbac"));
const GeneralRbac = lazy(() => import("./pages/Agentic/GeneralRbac"));
const Enrichment = lazy(() => import("./pages/Enrichment"));
const Security = lazy(() => import("./pages/Security"));
const ModelAbstraction = lazy(() => import("./pages/ModelAbstraction"));
const Models = lazy(() => import("./pages/Models"));
const Monetization = lazy(() => import("./pages/Monetization"));
const Developers = lazy(() => import("./pages/Developers"));
const Settings = lazy(() => import("./pages/Settings"));
const Connectors = lazy(() => import("./pages/Connectors"));
const GitConfig = lazy(() => import("./pages/Configs/GitConfig"));
const GCSConfig = lazy(() => import("./pages/Configs/GCSConfig"));
const ApiStandardLinting = lazy(() => import("./pages/Configs/ApiStandardLinting"));
const ApiCustomLinting = lazy(() => import("./pages/Configs/ApiCustomLinting"));
const AddGitConfig = lazy(() => import("./pages/ConfigActions/AddGitConfig"));
const AddGCSConfig = lazy(() => import("./pages/ConfigActions/AddGCSConfig"));

function ProtectedPageContent() {
    const { pageId, subPage, action } = useParams();

    const pageComponentMap = {
        onboarding: Onboarding,
        organizations: Onboarding,
        "access-center": AccessCenter,
        dashboard: Dashboard,
        "api-deploy": APIDeploy,
        "api-test": APITest,
        profile: Profile,
        security: SecurityCenter,
        routes: AIRoutes,
        "ai-proxy": AIProxies,
        "mcp-proxy": AIProxies,
        "agent-proxy": AIProxies,
        product: ProductsView,
        consumer: ConsumersView,
        developer: GatewayDeveloper,
        governance: GovernanceView,
        "agent-governance": AgentGovernance,
        "ai-governance": AiGovernance,
        "exception-management": ExceptionManagement,
        framework: FrameworkView,
        metrics: AIProxyMetrics,
        "error-codes": ErrorCodeAnalysis,
        latency: LatencyAnalysis,
        cache: CachePerformance,
        target: TargetPerformance,
        report: ObservabilityReport,
        monitoring: Monitoring,
        "cost-control": CostControl,
        trace: GatewayTrace,
        "enterprise-audit": EnterpriseAudit,
        environments: EnvironmentsConfig,
        "gateway-env": GatewayEnvironments,
        "cicd-automation": CicdAutomation,
        resilience: Resilience,
        rag: RAG,
        agents: Agents,
        "agent-harness": AgentHarness,
        "agent-inventory": AgentInventory,
        "agent-identity": AgentIdentity,
        "agent-rbac": AgentRbac,
        "general-rbac": GeneralRbac,
        enrichment: Enrichment,
        "security-hub": Security,
        "model-abstraction": ModelAbstraction,
        models: Models,
        monetize: Monetization,
        developers: Developers,
        settings: Settings,
        connectors: Connectors,
    };

    // Checked before the page lookup so "sync" isn't read as an AI Proxy name.
    if (subPage === "sync" && !action && SYNC_SCOPE_BY_PAGE[pageId]) {
        return <ResourceSyncWizard key={pageId} resourceScope={SYNC_SCOPE_BY_PAGE[pageId]} />;
    }

    // Onboarding deep routes:
    // /onboarding/business-units/:buId   → BusinessUnitDetail
    // /onboarding/projects/:projectId    → ProjectDetail
    // /onboarding/applications/:appId    → ApplicationDetail
    if (pageId === 'onboarding') {
        if (subPage === 'business-units' && action) return <BusinessUnitDetail />;
        if (subPage === 'projects' && action) return <ProjectDetail />;
        if (subPage === 'applications' && action) return <ApplicationDetail />;
    }

    //config routing
    if (pageId === "configs") {

        //config actions
        if (action === "add") {
            const addMap = {
                git: AddGitConfig,
                storage: AddGCSConfig,
                // "api-standard-linting": AddApiStandardLinting,
                // "api-custom-linting": AddApiCustomLinting,
            };

            const AddComponent =
                addMap[subPage] ||
                (() => <div>Add page not found: {subPage}</div>);

            return <AddComponent />;
        }
        const configMap = {
            git: GitConfig,
            storage: GCSConfig,
            "api-standard-linting": ApiStandardLinting,
            "api-custom-linting": ApiCustomLinting,
        };

        const ConfigComponent =
            configMap[subPage] ||
            (() => <div>Config not found: {subPage}</div>);

        return <ConfigComponent />;
    }

    const PageComponent =
        pageComponentMap[pageId] ||
        (() => <div>Page not found: /{pageId}</div>);

    // key={pageId} forces a full remount when the nav category changes (e.g.
    // AI Gateway -> MCP Gateway) instead of reusing the same component
    // instance — otherwise in-progress state (an open proxy detail view,
    // filters, the create modal) leaks across pages that just happen to
    // share a component, and only the route/active menu item updates.
    // subPage/action are forwarded generically so a page component (e.g.
    // AIProxies) can drive its own sub-navigation — a proxy's detail view
    // and Proxy Editor — off real URL segments instead of local state; pages
    // that don't use them simply ignore the extra props.
    return <PageComponent key={pageId} pageId={pageId} subPage={subPage} action={action} />;
}

export default function AppRoutes() {
    return (
        <Suspense fallback={<ProbestackSpinner/>}>
            <Routes>
                <Route path="ai-gateway" element={<Navigate to="ai-gateway/dashboard" replace />} />
                <Route path="ai-gateway/:pageId" element={<ProtectedPageContent />} />
                <Route path="ai-gateway/:pageId/:subPage" element={<ProtectedPageContent />} />
                <Route path="ai-gateway/:pageId/:subPage/:action" element={<ProtectedPageContent />} />
                <Route path="*" element={<Navigate to="ai-gateway/dashboard" replace />} />
            </Routes>
        </Suspense>
    );
}
